import React from 'react';
import { portfolio, LOGO } from '../mock';
import { Building2, DoorOpen, Car, Coffee, Landmark } from 'lucide-react';
import useReveal from '../hooks/useReveal';

/* Distinct 3D visual per corporate signage type */
function WorkVisual({ item }) {
  const type = item.type;

  if (type === 'wallgraphic') {
    return (
      <div style={{ position: 'relative', width: 180, height: 130 }}>
        {/* Wall */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #1F3438, #0F1F22)', border: '1px solid var(--line)' }}>
          {/* Peeling vinyl graphic */}
          <div className="vinyl-peel" style={{ position: 'absolute', top: 14, left: 14, right: 14, bottom: 14, padding: 12 }}>
            <div className="font-mono" style={{ fontSize: 8, letterSpacing: '0.22em' }}>WALL GRAPHIC</div>
            <div className="font-display" style={{ fontSize: 22, lineHeight: 1, marginTop: 4 }}>WORK.<br />PLAY.</div>
            <div style={{ height: 2, background: 'var(--warm-white)', width: '55%', marginTop: 6 }} />
          </div>
        </div>
      </div>
    );
  }

  if (type === 'wallart') {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div className="art-frame" style={{ width: 60, transform: 'rotate(-3deg)' }}>
          <div style={{ background: 'var(--orange)', height: 78 }} />
        </div>
        <div className="art-frame" style={{ width: 74 }}>
          <div style={{ background: 'var(--teal)', height: 94, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={LOGO} alt="" style={{ width: 46, height: 46, objectFit: 'contain' }} />
          </div>
        </div>
        <div className="art-frame" style={{ width: 58, transform: 'rotate(4deg)' }}>
          <div style={{ background: 'var(--teal-light)', height: 70 }} />
        </div>
      </div>
    );
  }

  if (type === 'wallart3d') {
    /* Extruded 3D wall art with layered depth */
    return (
      <div style={{ position: 'relative', width: 200, height: 140, perspective: 800 }}>
        <div style={{ position: 'absolute', inset: 0, transformStyle: 'preserve-3d', transform: 'rotateY(-14deg) rotateX(6deg)' }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{
              position: 'absolute',
              top: 20 + i * 4,
              left: 20 + i * 6,
              width: 100,
              height: 100,
              background: `linear-gradient(140deg, ${['#E97435', '#2A9AA0', '#4FBFB8', '#B85920'][i]} 0%, ${['#B85920', '#133A3F', '#2A9AA0', '#5a2810'][i]} 100%)`,
              borderRadius: 4,
              transform: `translateZ(${i * 12}px)`,
              boxShadow: '0 6px 12px -6px rgba(0,0,0,0.6)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {i === 3 && <img src={LOGO} alt="" style={{ width: 52, height: 52, objectFit: 'contain' }} />}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'hanging') {
    return (
      <div style={{ paddingTop: 30, width: 170 }}>
        <div className="chain-sign">
          <div className="chain-bracket left" style={{ top: -46 }} />
          <div className="chain-bracket right" style={{ top: -46 }} />
          <div className="chain left" style={{ top: -40, height: 40 }} />
          <div className="chain right" style={{ top: -40, height: 40 }} />
          <div className="sign-board" style={{ padding: '12px 16px' }}>
            <div style={{ textAlign: 'center' }}>
              <img src={LOGO} alt="" style={{ width: 42, height: 42, borderRadius: 6, background: 'var(--warm-white)', padding: 3 }} />
              <div className="font-mono" style={{ color: 'var(--teal-light)', fontSize: 7, letterSpacing: '0.22em', marginTop: 6 }}>HANGING SIGN</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'wallsign') {
    /* Wall-mounted acrylic sign with standoffs */
    return (
      <div style={{ position: 'relative', width: 200, height: 130 }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #1F3438, #0F1F22)', border: '1px solid var(--line)' }} />
        <div style={{
          position: 'absolute', top: 22, left: 22, right: 22, bottom: 22,
          background: 'linear-gradient(160deg, rgba(255,255,255,0.06), rgba(0,0,0,0.15))',
          border: '1px solid rgba(255,255,255,0.15)',
          backdropFilter: 'blur(4px)',
          boxShadow: '6px 6px 12px -6px rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <img src={LOGO} alt="" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          <div className="font-display" style={{ color: 'var(--paper)', fontSize: 18, letterSpacing: '0.12em' }}>VISTORA</div>
        </div>
        {/* Standoffs */}
        {[[18, 18], [18, 108], [178, 18], [178, 108]].map(([l, t], i) => (
          <div key={i} style={{ position: 'absolute', left: l, top: t, width: 6, height: 6, borderRadius: '50%', background: '#C69A5B', boxShadow: '0 1px 3px rgba(0,0,0,0.6)' }} />
        ))}
      </div>
    );
  }

  if (type === 'door') {
    return (
      <div style={{ position: 'relative', width: 130, height: 150 }}>
        {/* Door outline */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #2a1e14, #1a1210)', border: '2px solid #3a2a18', borderRadius: '3px 3px 0 0' }} />
        {/* Door knob */}
        <div style={{ position: 'absolute', top: '55%', right: 10, width: 6, height: 6, borderRadius: '50%', background: '#C69A5B', boxShadow: '0 1px 2px rgba(0,0,0,0.5)' }} />
        {/* Door plaque */}
        <div className="door-plaque" style={{ position: 'absolute', top: 30, left: 15, right: 15, padding: '10px 12px' }}>
          <span className="screw" style={{ top: 3, left: 3 }} />
          <span className="screw" style={{ top: 3, right: 3 }} />
          <span className="screw" style={{ bottom: 3, left: 3 }} />
          <span className="screw" style={{ bottom: 3, right: 3 }} />
          <div className="font-mono" style={{ fontSize: 7, letterSpacing: '0.22em', color: 'rgba(31,42,46,0.7)' }}>ROOM 302</div>
          <div className="font-display" style={{ fontSize: 13, letterSpacing: '0.06em' }}>BOARDROOM</div>
        </div>
      </div>
    );
  }

  if (type === 'pylon') {
    /* Tall vertical pylon sign */
    return (
      <div style={{ position: 'relative', width: 60 }}>
        <div style={{
          background: 'linear-gradient(180deg, var(--teal-2) 0%, var(--teal-dark) 100%)',
          padding: '18px 8px',
          minHeight: 160,
          boxShadow: 'inset 3px 0 8px rgba(255,255,255,0.15), inset -3px 0 8px rgba(0,0,0,0.3), 0 20px 40px -15px rgba(0,0,0,0.9)',
          borderRadius: 6,
          textAlign: 'center',
        }}>
          <img src={LOGO} alt="" style={{ width: 32, height: 32, borderRadius: 4, background: 'var(--warm-white)', padding: 2, marginBottom: 10 }} />
          <div className="font-display" style={{ color: 'var(--warm-white)', fontSize: 10, letterSpacing: '0.14em', writingMode: 'vertical-rl', transform: 'rotate(180deg)', margin: '0 auto', display: 'inline-block' }}>VISTORA</div>
        </div>
        <div style={{ position: 'absolute', bottom: -10, left: -6, right: -6, height: 14, background: 'linear-gradient(180deg, #2a2420, #14100c)', borderRadius: 2 }} />
      </div>
    );
  }

  if (type === 'building') {
    /* Building silhouette with mounted logo */
    return (
      <div style={{ position: 'relative', width: 180, height: 140 }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #1F3438 0%, #0F1F22 70%, #0a1416 100%)' }}>
          {/* Windows */}
          <div style={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
            {Array.from({ length: 18 }).map((_, i) => (
              <div key={i} style={{ height: 10, background: i % 3 === 0 ? 'rgba(232, 200, 130, 0.5)' : 'rgba(79, 191, 184, 0.1)', border: '1px solid rgba(255,255,255,0.05)' }} />
            ))}
          </div>
          {/* Logo mount */}
          <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: 'var(--warm-white)', padding: 8, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 0 24px rgba(232, 116, 53, 0.35)' }}>
            <img src={LOGO} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
            <div className="font-display" style={{ color: 'var(--ink)', fontSize: 12, letterSpacing: '0.12em' }}>VISTORA</div>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'workstation') {
    /* Workstation nameplate row */
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {['A.01 · MARKETING', 'A.02 · FINANCE', 'A.03 · LEGAL'].map((label, i) => (
          <div key={i} style={{
            background: 'linear-gradient(160deg, #F5F1E8, #E8DFD0)',
            padding: '8px 14px',
            boxShadow: '0 3px 0 var(--teal-2), 0 6px 10px -4px rgba(0,0,0,0.5)',
            minWidth: 170,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span className="font-mono" style={{ color: 'var(--ink)', fontSize: 10, letterSpacing: '0.2em', fontWeight: 600 }}>{label}</span>
            <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--orange)' }} />
          </div>
        ))}
      </div>
    );
  }

  if (type === 'parking') {
    return (
      <div style={{ position: 'relative', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        {/* P Sign */}
        <div style={{
          width: 70, height: 90,
          background: 'linear-gradient(180deg, #2A9AA0, #133A3F)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '3px solid var(--warm-white)',
          boxShadow: '0 10px 20px -8px rgba(0,0,0,0.6)',
        }}>
          <div className="font-display" style={{ color: 'var(--warm-white)', fontSize: 54, lineHeight: 1 }}>P</div>
        </div>
        {/* Directional info */}
        <div style={{ background: 'var(--ink-2)', border: '1px solid var(--teal)', padding: '10px 14px', minWidth: 100 }}>
          <div className="font-mono" style={{ color: 'var(--teal-light)', fontSize: 8, letterSpacing: '0.22em' }}>LEVEL B1</div>
          <div className="font-display" style={{ color: 'var(--warm-white)', fontSize: 12, letterSpacing: '0.1em', marginTop: 4 }}>PARKING</div>
          <div className="font-mono" style={{ color: 'var(--orange-2)', fontSize: 8, letterSpacing: '0.18em', marginTop: 6 }}>→ 20 SLOTS</div>
        </div>
      </div>
    );
  }

  if (type === 'amenities') {
    /* Row of amenity icons */
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        {[
          { Icon: Coffee, label: 'CAFE' },
          { Icon: DoorOpen, label: 'REST' },
          { Icon: Car, label: 'PARK' },
        ].map((a, i) => (
          <div key={i} style={{
            background: 'var(--ink-2)',
            border: '1px solid var(--teal-2)',
            padding: '12px 10px',
            width: 60,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            boxShadow: '0 8px 16px -6px rgba(0,0,0,0.5)',
          }}>
            <a.Icon size={22} color="var(--teal-light)" strokeWidth={1.6} />
            <div className="font-mono" style={{ color: 'var(--paper)', fontSize: 8, letterSpacing: '0.2em' }}>{a.label}</div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'facade') {
    /* Building facade with big channel letters */
    return (
      <div style={{ position: 'relative', width: 200, height: 130 }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #1F3438 0%, #0F1F22 100%)', border: '1px solid var(--line)' }}>
          {/* Panel grid (ACP) */}
          <div style={{ position: 'absolute', inset: 6, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} style={{ background: 'linear-gradient(140deg, rgba(255,255,255,0.03), transparent)', border: '1px solid rgba(255,255,255,0.04)' }} />
            ))}
          </div>
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)', display: 'flex', gap: 2, justifyContent: 'center' }}>
            <span className="channel-letter" style={{ fontSize: 44 }}>V</span>
            <span className="channel-letter orange" style={{ fontSize: 44 }}>S</span>
            <span className="channel-letter" style={{ fontSize: 44 }}>T</span>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function WorkTile({ item }) {
  return (
    <div style={{
      minWidth: 340, height: 300, margin: '0 10px',
      position: 'relative', overflow: 'hidden',
      background: 'var(--ink-2)',
      border: '1px solid var(--line)',
      transition: 'transform 0.6s cubic-bezier(.2,.8,.2,1), border-color 0.3s',
      borderRadius: 6,
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-10px) rotateX(4deg)'; e.currentTarget.style.borderColor = 'var(--teal-light)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.borderColor = 'var(--line)'; }}
    >
      <div style={{ position: 'absolute', top: 16, left: 20, right: 20, display: 'flex', justifyContent: 'space-between', zIndex: 3 }}>
        <span className="font-mono" style={{ color: 'var(--muted)', fontSize: 10, letterSpacing: '0.2em' }}>#{String(item.id).padStart(3, '0')}</span>
        <span className="font-mono" style={{ color: 'var(--orange)', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 600 }}>{item.category}</span>
      </div>

      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 20 }}>
        <WorkVisual item={item} />
      </div>

      <div style={{ position: 'absolute', bottom: 18, left: 20, right: 20 }}>
        <div className="font-display" style={{ color: 'var(--paper)', fontSize: 20, letterSpacing: '-0.01em' }}>{item.title}</div>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const [ref, inView] = useReveal(0.1);
  const firstRow = portfolio.slice(0, 6);
  const secondRow = portfolio.slice(6);

  return (
    <section id="work" ref={ref} className="section" style={{ background: 'var(--ink-2)', position: 'relative' }}>
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-8 items-end" style={{ marginBottom: 50 }}>
          <div className={`lg:col-span-7 reveal ${inView ? 'in-view' : ''}`}>
            <div className="eyebrow" style={{ marginBottom: 24 }}>§ 03 · Corporate Portfolio</div>
            <h2 className="section-title">Selected <em>work</em>.</h2>
          </div>
          <div className={`lg:col-span-5 reveal delay-2 ${inView ? 'in-view' : ''}`}>
            <p className="font-serif-i" style={{ color: 'var(--smoke)', fontSize: 17, lineHeight: 1.6 }}>
              Wall graphics, 3D wall art, hanging &amp; door signage, pylons, facades, workstation nameplates, parking &amp; amenities &mdash; the full corporate signage stack.
            </p>
          </div>
        </div>
      </div>

      <div className="marquee-wrap" style={{ overflow: 'hidden', maskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)' }}>
        <div className="marquee-track flex" style={{ width: 'max-content' }}>
          {[...firstRow, ...firstRow].map((p, i) => <WorkTile key={`a${i}`} item={p} />)}
        </div>
      </div>

      <div className="marquee-wrap" style={{ overflow: 'hidden', marginTop: 20, maskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)' }}>
        <div className="marquee-track reverse flex" style={{ width: 'max-content' }}>
          {[...secondRow, ...secondRow].map((p, i) => <WorkTile key={`b${i}`} item={p} />)}
        </div>
      </div>

      <div className="container-x" style={{ marginTop: 60, textAlign: 'center' }}>
        <a href="#contact" className="btn-ghost" style={{ textDecoration: 'none' }}>
          <Building2 size={14} color="var(--teal-light)" /> Commission Your Corporate Project
        </a>
      </div>
    </section>
  );
}
